import { ADMIN_PRINCIPAL_ID, PrincipalType } from '@lucidlink/dto/validation';
import { ForbiddenError } from '#fetcher/fetch-error.types';
/**
 * Maps a single principal ID to a Principal object with member, group, or service account information
 * @param principalId - The principal ID to map
 * @param membersMap - Map of member IDs to member data
 * @param groupsMap - Map of group IDs to group data
 * @param serviceAccountsMap - Map of service account IDs to service account data
 * @returns Principal object or null if the principal is not found or is ADMIN_PRINCIPAL_ID
 */
export const remapSinglePrincipalId = (principalId, membersMap, groupsMap, serviceAccountsMap) => {
    if (principalId == ADMIN_PRINCIPAL_ID) {
        return null;
    }
    const member = membersMap.get(principalId);
    if (member) {
        return {
            type: PrincipalType.MEMBER,
            principalId,
            member,
        };
    }
    const group = groupsMap.get(principalId);
    if (group) {
        return {
            type: PrincipalType.GROUP,
            principalId,
            group,
        };
    }
    const serviceAccount = serviceAccountsMap.get(principalId);
    if (serviceAccount) {
        return {
            type: PrincipalType.SERVICE_ACCOUNT,
            principalId,
            serviceAccount,
        };
    }
    return null;
};
/**
 * Remaps WebFs access info to SDK format, filtering out invalid principals
 * @param fsEntryId - The file system entry ID
 * @param fsEntryPath - The file system entry path
 * @param webFsAccess - Array of WebFs principal access entries
 * @param membersMap - Map of member IDs to member data
 * @param groupsMap - Map of group IDs to group data
 * @param serviceAccountsMap - Map of service account IDs to service account data
 * @returns FsEntryAccessList with mapped principal information
 */
export const remapAccessInfo = ({ fsEntryId, fsEntryPath, fsEntryType, principalsAccess: webFsAccess, }, membersMap, groupsMap, serviceAccountsMap) => {
    const principalsAccess = [];
    for (const { principalId, permissions } of webFsAccess) {
        const principal = remapSinglePrincipalId(principalId, membersMap, groupsMap, serviceAccountsMap);
        if (!principal) {
            continue;
        }
        principalsAccess.push({
            principal,
            permissions,
        });
    }
    return {
        fsEntry: fsEntryId,
        entryPath: fsEntryPath,
        entryType: fsEntryType,
        principalsAccess,
    };
};
/**
 * Maps WebFs access items to SDK format with principal information, filtering out invalid principals
 * @param items - Array of WebFs access info entries
 * @param membersMap - Map of member IDs to member data
 * @param groupsMap - Map of group IDs to group data
 * @param serviceAccountsMap - Map of service account IDs to service account data
 * @returns Array of access items with principal info, null entries filtered out
 */
export const mapAccessItemsWithPrincipalInfo = (items, membersMap, groupsMap, serviceAccountsMap) => {
    return items
        .map(({ fsEntryId, fsEntryPath, fsEntryType, principalId, permissions, }) => {
        const principal = remapSinglePrincipalId(principalId, membersMap, groupsMap, serviceAccountsMap);
        if (!principal) {
            return null;
        }
        return {
            fsEntryId,
            fsEntryPath,
            fsEntryType,
            principalInfo: principal,
            permissions,
        };
    })
        .filter((item) => item !== null);
};
/**
 * Maps WebFs file system entries access to SDK format
 * @param access - Array of WebFs access entries
 * @returns Array of mapped file system entries access
 */
export const mapFsEntriesAccess = (access) => {
    return access.map(({ fsEntryId, fsEntryPath, fsEntryType, permissions }) => ({
        fsEntry: fsEntryId,
        entryPath: fsEntryPath,
        entryType: fsEntryType,
        permissions,
    }));
};
/**
 * Fetches members, groups, and service accounts maps with error handling for ForbiddenError
 * @param membersMap - Time-cached value containing members map
 * @param groupsMap - Time-cached value containing groups map
 * @param serviceAccountsMap - Time-cached value containing service accounts map
 * @param catchAllErrors - If true, catches all errors; if false, only catches ForbiddenError
 * @returns Object containing membersMap, groupsMap, and serviceAccountsMap
 */
export const getMapsWithFallback = async (membersMap, groupsMap, serviceAccountsMap, catchAllErrors = false) => {
    const members = await membersMap.get();
    const groups = await groupsMap.get().catch((error) => {
        if (catchAllErrors || error instanceof ForbiddenError) {
            return new Map();
        }
        throw error;
    });
    const serviceAccounts = await serviceAccountsMap.get().catch((error) => {
        if (catchAllErrors || error instanceof ForbiddenError) {
            return new Map();
        }
        throw error;
    });
    return {
        membersMap: members,
        groupsMap: groups,
        serviceAccountsMap: serviceAccounts,
    };
};
